/**
* Localized translation & legalisation comparison cluster —
* /{locale}/translation/compare/{slug}.
*
* Hand-written per-locale copy (NOT machine translation of the Thai
* cluster). ASCII slugs mirror the Thai slugs; the mapping lives in
* MLT_COMPARE_SLUG_MAP so hreflang stays reciprocal.
*
* Accuracy policy (verified 2026-08-02)
* - Thailand has NO statutory register of sworn/court-appointed translators.
* Certification is done by (a) the translator or agency self-certifying,
* (b) a Notarial Services Attorney licensed by the Lawyers Council of
* Thailand certifying signatures/copies, or (c) the Legalization Division,
* Department of Consular Affairs, Ministry of Foreign Affairs.
* - MFA legalisation fee: THB 200 per stamp (normal) / THB 400 per stamp
* (express) — consular.mfa.go.th; see src/data/official-fees.ts.
* - Thailand acceded to the HCCH Apostille Convention on 30 June 2026; entry
* into force for Thailand is 28 February 2027 (hcch.net). Until then the
* MFA → destination-embassy chain still applies.
* - NAATI is the Australian credentialing body (naati.com.au), not a Thai
* authority.
* - All timings and costs are approximate ranges. Acceptance is always at the
* receiving authority's discretion; no outcome is guaranteed.
*/

export type {
  MltCompareLocale,
  MltCompareLocaleRow,
  MltCompareLocaleItem,
  MltCompareLocaleCopy,
} from "./compare-locale-types";
import type { MltCompareLocale, MltCompareLocaleCopy } from "./compare-locale-types";

const EN_TAIL =
  "Information current as of August 2026. Requirements change — confirm the exact certification format with the authority that will receive the document before you file. Acceptance is at their discretion.";
const ZH_TAIL =
  "信息更新至 2026 年 8 月。各机构要求可能变动，递交前请先向收件机构确认所需的认证形式；是否受理由该机构裁量。";

export const MLT_COMPARE_LOCALE: Record<MltCompareLocale, MltCompareLocaleCopy> = {
  en: {
    hreflang: "en",
    hubTitle: "Certified vs Notarised vs MFA-Legalised Translation in Thailand",
    hubDescription:
      "Point-by-point comparisons of every Thai document-translation route: translator certification, lawyer notarisation, MFA consular legalisation, NAATI translations for Australia and European sworn translators.",
    hubH1: "Thai translation certification routes — which one does your authority actually accept?",
    hubLead:
      "The most expensive question in Thai document work is \"how far up the chain do I need to certify?\", because choosing the wrong layer means translating and paying twice. Thailand has no register of sworn translators, so the practical routes are translator self-certification, a Notarial Services Attorney certifying the signature, and legalisation of the translation by the Department of Consular Affairs at THB 200 per stamp (THB 400 express). Each comparison below shows exactly what each layer proves, what it costs and how long it takes.",
    labels: {
      table: "Point-by-point comparison",
      chooseWhen: "choose this when",
      faq: "Frequently asked questions",
      other: "Other comparisons",
      breadcrumbHome: "Home",
      breadcrumbPillar: "Translation & legalisation",
      breadcrumbCompare: "Compare routes",
      disclaimer: EN_TAIL,
      cta: "Send us a photo of the document plus the written requirement from the receiving authority, and we will map the exact chain — translation, lawyer certification, MFA, embassy — before you pay for anything. Call 094-895-8999 or LINE @Thainotary.",
    },
    items: [
      {
        slug: "certified-vs-notarised-translation",
        thSlug: "certified-translation-vs-notarised-translation",
        title: "Certified translation vs notarised translation — what the difference really is",
        description:
          "A certified translation attests to accuracy; a notarised translation attests to the identity and signature of the person who certified it. Compare cost, turnaround and which authorities accept each.",
        h1: "Certified translation vs notarised translation",
        aLabel: "Certified translation",
        bLabel: "Notarised translation",
        answer:
          "A certified translation is a statement by the translator or agency that the translation is complete and accurate; nothing about the translator's identity is independently verified. A notarised translation adds a second layer: a Notarial Services Attorney witnesses and certifies the signature on that statement. Notarisation never verifies the accuracy of the wording — it verifies who signed. Universities and most private recipients accept certification alone; embassies, courts and foreign registries usually want the notarised layer, and some also require MFA legalisation on top.",
        rows: [
          {
            aspect: "What is actually attested",
            a: "Completeness and accuracy of the translation, stated by the translator",
            b: "Identity and signature of the person who signed the certification",
          },
          {
            aspect: "Who signs",
            a: "Translator or translation company representative",
            b: "Notarial Services Attorney licensed by the Lawyers Council of Thailand",
          },
          { aspect: "Typical turnaround", a: "Same day to 2 working days", b: "Add roughly 1 working day" },
          { aspect: "Typical cost", a: "Per-page translation fee only", b: "Translation fee plus a per-signature certification fee" },
          {
            aspect: "Usually enough for",
            a: "Universities, employers, banks, internal company files",
            b: "Embassies, foreign courts, company registries, immigration authorities",
          },
          {
            aspect: "Next step in the chain",
            a: "Often needs notarisation afterwards",
            b: "May still need MFA legalisation and destination-embassy legalisation",
          },
        ],
        chooseA: [
          "The recipient's written instruction only says \"certified translation\"",
          "The document stays inside Thailand or goes to a private organisation",
          "You need the fastest and cheapest route and can re-certify later if asked",
        ],
        chooseB: [
          "The recipient asks for a notary, notarisation or \"sworn\" signature",
          "The document is going to a foreign court, registry or embassy",
          "The next step in the chain is MFA or embassy legalisation",
        ],
        faqs: [
          {
            q: "Does notarisation make the translation legally accurate?",
            a: "No. Notarisation certifies who signed the certification statement, not the quality of the wording. Accuracy remains the responsibility of the translator who signed. This is the single most common misunderstanding we correct for clients.",
          },
          {
            q: "Can the same person translate and notarise?",
            a: "In practice they are separate roles: the translator signs the accuracy statement and a Notarial Services Attorney certifies that signature. Our team coordinates both so the document leaves with one consistent set of stamps.",
          },
          {
            q: "Will an embassy accept a certified translation without notarisation?",
            a: "Some do and many do not — it depends entirely on the embassy and the document type. Always ask for the requirement in writing, because embassies change their formats without notice.",
          },
          {
            q: "How much does the notarised layer add?",
            a: "It adds a per-signature attorney certification fee and usually about one working day. The exact amount depends on the number of signatures and pages, so we quote after seeing the document.",
          },
          {
            q: "Does either route need MFA legalisation?",
            a: "If the document is going abroad, very often yes. The Department of Consular Affairs charges THB 200 per stamp for the normal service and THB 400 per stamp for the express service.",
          },
          {
            q: "Is an apostille an option instead?",
            a: "Not yet. Thailand acceded to the HCCH Apostille Convention on 30 June 2026 and it enters into force for Thailand on 28 February 2027. Until that date the MFA-plus-embassy chain remains the route.",
          },
        ],
      },
      {
        slug: "mfa-legalisation-vs-lawyer-certification",
        thSlug: "รับรองคำแปล-กงสุล-vs-ทนาย",
        title: "MFA consular legalisation vs lawyer certification of a translation",
        description:
          "Compare legalisation of a translation by Thailand's Department of Consular Affairs with certification by a Notarial Services Attorney: fees, turnaround, language limits and which one embassies want.",
        h1: "MFA legalisation vs lawyer certification — which layer does the translation need?",
        aLabel: "MFA consular legalisation",
        bLabel: "Lawyer certification",
        answer:
          "The Department of Consular Affairs legalises translations between Thai and English (and Thai-language originals) as a government act, charging THB 200 per stamp for the normal service and THB 400 per stamp for the express service. A Notarial Services Attorney certifies signatures and copies as a private professional act, works in any language pair and is far faster. Foreign government recipients and destination embassies generally require the MFA stamp; private recipients and many onward-notarisation chains only need the lawyer layer.",
        rows: [
          { aspect: "Nature of the act", a: "Government legalisation by the MFA", b: "Private professional certification by a licensed attorney" },
          { aspect: "Official fee", a: "THB 200 per stamp normal, THB 400 per stamp express", b: "Professional fee per signature, quoted per document" },
          { aspect: "Typical turnaround", a: "About 2–3 working days normal, same/next day express", b: "Usually same day or next day by appointment" },
          { aspect: "Language coverage", a: "Mainly Thai and English", b: "Any language pair we can source a qualified translator for" },
          { aspect: "Accepted by", a: "Destination embassies and foreign government bodies", b: "Private recipients, and as the step before MFA" },
          { aspect: "Position in the chain", a: "Usually after translation and lawyer certification", b: "Usually the first certification step" },
        ],
        chooseA: [
          "A foreign government body or embassy asks for MFA or consular legalisation",
          "The document is Thai↔English and will be filed abroad",
          "The onward step is legalisation at the destination embassy in Bangkok",
        ],
        chooseB: [
          "You need a language pair the MFA does not routinely legalise",
          "The recipient is a private organisation or overseas lawyer",
          "You need a fast first-layer certification before the MFA appointment",
        ],
        faqs: [
          {
            q: "Can I go straight to the MFA without lawyer certification?",
            a: "Sometimes, depending on the document. Many documents need a certified translation attached in the MFA's required format first, which is why we check the document before booking the appointment.",
          },
          {
            q: "Is the express MFA service worth it?",
            a: "The express service costs THB 400 per stamp instead of THB 200 and shortens the wait considerably. If your submission deadline is tight it is usually the cheaper option overall.",
          },
          {
            q: "Does the MFA legalise translations into Japanese or Chinese?",
            a: "The consular legalisation service is centred on Thai and English. For other target languages the practical route is a certified translation plus lawyer certification, then legalisation of the underlying Thai or English document.",
          },
          {
            q: "Do I need both layers?",
            a: "For most documents heading abroad, yes — lawyer certification first, then MFA, then the destination embassy. We confirm the sequence with the receiving authority's written requirement before starting.",
          },
          {
            q: "Will an apostille replace the MFA stamp?",
            a: "From 28 February 2027, when the Apostille Convention enters into force for Thailand, apostilles will replace the embassy step for member states. Until then the full chain still applies.",
          },
          {
            q: "How many stamps will I be charged for?",
            a: "The MFA charges per stamp, so a multi-document bundle multiplies the fee. Send us the list and we will estimate the stamp count before you go.",
          },
        ],
      },
      {
        slug: "naati-vs-thai-certified-translation",
        thSlug: "naati-vs-คำแปลรับรองไทย",
        title: "NAATI translation vs Thai certified translation — for Australian submissions",
        description:
          "NAATI is Australia's translator credentialing body. Compare a NAATI-certified translation with a Thai certified and legalised translation, and see which Australian authorities accept which.",
        h1: "NAATI translation vs Thai certified translation",
        aLabel: "NAATI-certified translation",
        bLabel: "Thai certified translation",
        answer:
          "NAATI is the Australian national credentialing authority for translators; a NAATI-certified translation carries the practitioner's stamp and number and is the default expectation of Australian government agencies such as the Department of Home Affairs. A Thai certified translation, even with lawyer certification and MFA legalisation, follows the Thai chain and is aimed at Thai and third-country authorities. Neither is universally superior — the correct choice is dictated by the authority that will read the document.",
        rows: [
          { aspect: "Credentialing body", a: "NAATI, Australia (naati.com.au)", b: "No Thai register; translator or agency self-certifies" },
          { aspect: "Visible marking", a: "Practitioner stamp with certification number and expiry", b: "Certification statement, translator signature, agency seal" },
          { aspect: "Primary audience", a: "Australian government, universities, courts", b: "Thai authorities, embassies in Bangkok, third countries" },
          { aspect: "Legalisation needed", a: "Usually not for Australian domestic use", b: "Often followed by lawyer certification and MFA legalisation" },
          { aspect: "Where it is produced", a: "By a credentialed practitioner, wherever they are based", b: "In Thailand, matched to the Thai chain of stamps" },
          { aspect: "Typical turnaround", a: "Depends on practitioner availability for the language pair", b: "Same day to a few working days plus legalisation time" },
        ],
        chooseA: [
          "You are filing with an Australian government department or university",
          "The written requirement names NAATI explicitly",
          "The document will be used inside Australia rather than presented in Thailand",
        ],
        chooseB: [
          "The recipient is a Thai authority, an embassy in Bangkok or a non-Australian country",
          "The chain continues through the MFA and a destination embassy",
          "You need a language pair with no available NAATI practitioner",
        ],
        faqs: [
          {
            q: "Is NAATI recognised by Thai government offices?",
            a: "NAATI is an Australian credential. Thai offices look for a certified translation in their own accepted format, so a NAATI translation may need to be recertified locally for Thai use.",
          },
          {
            q: "Can one document carry both certifications?",
            a: "Yes — some clients need a NAATI version for Australia and a separately certified Thai version for local filing. They are prepared as two deliverables from the same source document.",
          },
          {
            q: "Does the Department of Home Affairs always require NAATI?",
            a: "Its published guidance expects translations by appropriately credentialed translators, and NAATI is the standard credential in Australia. Always follow the wording of the specific application guidance you are filing under.",
          },
          {
            q: "Do NAATI translations need MFA legalisation?",
            a: "For Australian domestic use, generally no. If the same document is then presented to a Thai authority or a third-country embassy, the Thai chain may be requested.",
          },
          {
            q: "Which is faster?",
            a: "Neither route is inherently faster; availability of a practitioner for your language pair and the MFA queue are the usual bottlenecks. We give a realistic range once we know the language and destination.",
          },
          {
            q: "What if I am not sure which the authority wants?",
            a: "Ask them in writing and send us the reply. Choosing on a guess is the main reason documents are translated twice.",
          },
        ],
      },
      {
        slug: "sworn-translator-vs-thailand",
        thSlug: "sworn-translator-vs-ไทย",
        title: "European sworn translators vs Thailand's certification system",
        description:
          "Germany, France, Spain and Italy appoint court-sworn translators. Thailand does not. Compare the two systems and see how to satisfy a European authority from Bangkok.",
        h1: "Sworn translator (Europe) vs Thailand's certification system",
        aLabel: "European sworn translator",
        bLabel: "Thai certification chain",
        answer:
          "Several European countries appoint translators through their courts or ministries; those translators sign and stamp under oath and their translations are accepted directly by domestic authorities. Thailand has no equivalent register, so a Thai document destined for such a country is normally handled as a certified translation, certified by a Notarial Services Attorney and legalised by the Department of Consular Affairs, then legalised by the destination embassy — or, in some cases, translated in the destination country by a locally sworn translator after the Thai original is legalised.",
        rows: [
          { aspect: "Legal basis", a: "Court or ministry appointment under national law", b: "No sworn-translator register; certification by contract and professional rules" },
          { aspect: "Who signs", a: "The sworn translator, under oath", b: "Translator plus Notarial Services Attorney" },
          { aspect: "Accepted directly by", a: "Domestic authorities in the appointing country", b: "Thai authorities and, after legalisation, foreign recipients" },
          { aspect: "Where the work happens", a: "Usually in the destination country", b: "In Thailand, before the document leaves" },
          { aspect: "Legalisation of the original", a: "Still usually required on the Thai original", b: "MFA legalisation at THB 200 per stamp, THB 400 express" },
          { aspect: "Risk if you choose wrong", a: "Translation may be rejected if done outside the register", b: "May need re-translation on arrival by a local sworn translator" },
        ],
        chooseA: [
          "The destination authority names a court-sworn or ministry-appointed translator",
          "You have a contact or agent in the destination country who can arrange it",
          "The document is short and re-translation on arrival is cheap",
        ],
        chooseB: [
          "The Thai original must be legalised before it leaves Thailand anyway",
          "The destination will accept a legalised Thai certified translation",
          "You need the whole package prepared before departure",
        ],
        faqs: [
          {
            q: "Can a Thai translator become a sworn translator?",
            a: "Not under Thai law — there is no such register in Thailand. Some Thai nationals hold appointments in other countries under that country's rules, which is a different thing.",
          },
          {
            q: "Will Germany accept a Thai certified translation?",
            a: "German authorities commonly require a translation by a translator sworn in Germany, though practice varies by state and office. Ask the specific office in writing before paying for a translation in Thailand.",
          },
          {
            q: "Should I legalise the Thai original even if I translate abroad?",
            a: "Usually yes. The destination will want proof that the Thai document itself is genuine, which is what MFA and embassy legalisation provide.",
          },
          {
            q: "Does the 2027 apostille change this?",
            a: "From 28 February 2027, apostilles will replace embassy legalisation for convention members, which simplifies proving the original. The sworn-translator requirement is separate and is unaffected.",
          },
          {
            q: "What does this cost in practice?",
            a: "Costs split between the Thai side — translation, attorney certification and MFA stamps at THB 200 or 400 each — and any translation done in the destination country. We quote only the Thai side.",
          },
          {
            q: "Can you coordinate both sides?",
            a: "We prepare and legalise the Thai side and hand over a package that a destination sworn translator can work from directly, which avoids duplicated work.",
          },
        ],
      },
      {
        slug: "translate-into-english-vs-target-language",
        thSlug: "แปลเป็นอังกฤษ-vs-แปลเป็นภาษาปลายทาง",
        title: "Translate into English or into the destination language?",
        description:
          "English is the pivot language for Thai legalisation, but many authorities want their own language. Compare the two strategies on cost, chain length and rejection risk.",
        h1: "Translate into English vs translate into the destination language",
        aLabel: "Into English",
        bLabel: "Into the destination language",
        answer:
          "English is the pivot language of Thai document work: the Department of Consular Affairs legalises Thai↔English routinely, so an English translation moves through the chain with the least friction. But a destination authority that reads only its own language may reject an English version or demand a second translation on arrival. The rule that saves money is to follow the written requirement literally — if it names the language, translate into that language, and use English only as the pivot for the legalisation stamps.",
        rows: [
          { aspect: "MFA legalisation", a: "Routine for Thai↔English", b: "Often not available; legalise the Thai or English version instead" },
          { aspect: "Chain length", a: "Shortest — one translation, one chain", b: "Longer, and may add a translation in the destination country" },
          { aspect: "Cost", a: "Lower per page for most documents", b: "Higher, especially for less common languages" },
          { aspect: "Rejection risk", a: "Higher where the authority reads only its own language", b: "Lower when the requirement names that language" },
          { aspect: "Turnaround", a: "Fastest, translators widely available", b: "Depends on availability for the language pair" },
          { aspect: "Best used for", a: "Embassies in Bangkok, international schools, banks", b: "Foreign registries, courts and municipal offices abroad" },
        ],
        chooseA: [
          "The requirement says \"English translation\" or does not name a language",
          "The next step is legalisation by the MFA and an embassy in Bangkok",
          "The recipient is an international organisation that works in English",
        ],
        chooseB: [
          "The written requirement names the destination language",
          "The document is filed with a local registry, court or municipality abroad",
          "A previous submission in English was returned",
        ],
        faqs: [
          {
            q: "Can I submit both versions?",
            a: "Often yes, and for high-stakes filings it is a sensible hedge. Confirm the recipient accepts a bilingual bundle rather than a single language.",
          },
          {
            q: "Does the MFA legalise a Thai-to-Japanese translation?",
            a: "Consular legalisation is centred on Thai and English. For Japanese we normally legalise the Thai or English version and attach the Japanese translation with a certification statement.",
          },
          {
            q: "Is a relay translation via English acceptable?",
            a: "It is common and usually accepted, but every relay step adds a chance of terminology drift. For legal instruments we prefer translating directly from the Thai source.",
          },
          {
            q: "Which is cheaper overall?",
            a: "English is cheaper up front. It stops being cheaper the moment the destination rejects it and you pay for a second translation abroad.",
          },
          {
            q: "How do I get the requirement in writing?",
            a: "Ask the receiving office for their document checklist by email or download it from their official site. Send it to us and we will match the format exactly.",
          },
          {
            q: "Do names have to match the passport?",
            a: "Yes. Name spellings should follow the passport exactly, since a mismatch between passport and translation is one of the most frequent reasons documents are sent back.",
          },
        ],
      },
      {
        slug: "diy-translation-vs-professional-team",
        thSlug: "แปลเอง-vs-ใช้ทีมแปลรับรอง",
        title: "DIY translation vs a certified translation team",
        description:
          "Self-translation is free until it is rejected. Compare cost, certification eligibility, formatting requirements and rework risk for Thai official documents.",
        h1: "Translating it yourself vs using a certified translation team",
        aLabel: "Do it yourself",
        bLabel: "Certified translation team",
        answer:
          "You may translate your own documents, but almost no authority will accept a translation certified by the person who benefits from it, and most Thai official documents have a fixed layout — stamps, registration numbers, issuing office — that must be reproduced in the translation. A certified team signs the accuracy statement, produces the format the MFA and embassies expect, and can carry the document through lawyer certification and legalisation. The real comparison is not price against zero, but price against the cost of a rejected filing.",
        rows: [
          { aspect: "Cost up front", a: "None", b: "Per-page fee, quoted before work starts" },
          { aspect: "Who can certify it", a: "Not the applicant themselves in almost all cases", b: "Translator or agency, then a Notarial Services Attorney" },
          { aspect: "Format compliance", a: "Frequently missing seals, registration numbers and layout", b: "Built to the layout the MFA and embassies expect" },
          { aspect: "Legalisation eligibility", a: "Usually refused at the counter", b: "Prepared to pass the MFA check" },
          { aspect: "Rework risk", a: "High — a rejected filing can cost weeks", b: "Low; corrections handled before submission" },
          { aspect: "Suitable for", a: "Personal understanding, internal drafts", b: "Any document that will be filed officially" },
        ],
        chooseA: [
          "You only need to understand the content of a document",
          "The recipient explicitly allows an uncertified translation",
          "It is an internal working draft with no filing deadline",
        ],
        chooseB: [
          "The document will be filed with any government body or embassy",
          "The chain continues through lawyer certification or MFA legalisation",
          "A deadline means a rejection would be expensive",
        ],
        faqs: [
          {
            q: "Can I translate my own documents and have a lawyer certify them?",
            a: "A lawyer certifies the signature on the accuracy statement, and an applicant certifying their own supporting evidence is normally refused by the recipient. Independent certification avoids that objection.",
          },
          {
            q: "Is machine translation acceptable?",
            a: "Not for official filings. Beyond accuracy, official Thai documents carry seals, registration numbers and formatting that machine output does not reproduce correctly.",
          },
          {
            q: "What is the most common reason documents come back?",
            a: "Name spellings that do not match the passport, missing reproduction of seals and stamps, and a certification format the recipient does not accept.",
          },
          {
            q: "How much does professional translation cost?",
            a: "It is quoted per page and depends on the language pair and document complexity. Send a photo and we will quote before any work starts.",
          },
          {
            q: "Can you fix a translation I already have?",
            a: "Yes. We review and re-certify existing translations where the content is sound; where it is not, re-translating is usually faster than editing.",
          },
          {
            q: "How long does the full chain take?",
            a: "As a rough guide: translation in one to two working days, attorney certification about a day, MFA legalisation two to three working days normal or faster with the express service, then whatever the destination embassy requires.",
          },
        ],
      },
    ],
  },

  zh: {
    hreflang: "zh",
    hubTitle: "泰国文件翻译认证路线对比：翻译社认证、律师公证、外交部认证",
    hubDescription:
      "逐项对比泰国各类翻译认证路线：译者认证、公证律师认证、外交部领事司认证、澳洲 NAATI 翻译与欧洲宣誓翻译，帮助您一次选对。",
    hubH1: "泰国翻译认证路线对比：收件机构究竟要哪一层？",
    hubLead:
      "泰国文件办理中最花钱的问题是「到底要认证到哪一层」，因为选错就得重译、重缴费。泰国没有宣誓翻译师登记制度，实际可行的路线只有三条：译者或翻译社自行出具准确性声明、由泰国律师公会核准的公证律师认证签名、以及由外交部领事司对译文做认证（每枚印章 200 泰铢，加急 400 泰铢）。以下每一组对比都说明各层认证证明什么、花多少钱、需要多久。",
    labels: {
      table: "逐项对比",
      chooseWhen: "适用情形",
      faq: "常见问题",
      other: "其他对比主题",
      breadcrumbHome: "首页",
      breadcrumbPillar: "翻译与认证",
      breadcrumbCompare: "路线对比",
      disclaimer: ZH_TAIL,
      cta: "请把文件照片和收件机构的书面要求发给我们，我们会先帮您理清完整链条（翻译 → 律师认证 → 外交部 → 使馆），再开始收费。电话 094-895-8999，LINE @Thainotary。",
    },
    items: [
      {
        slug: "certified-vs-notarised-translation",
        thSlug: "certified-translation-vs-notarised-translation",
        title: "认证翻译与公证翻译的真正差别",
        description:
          "认证翻译证明译文准确，公证翻译证明签名者身份。对比费用、时效以及哪些机构接受哪一种。",
        h1: "认证翻译 vs 公证翻译",
        aLabel: "认证翻译",
        bLabel: "公证翻译",
        answer:
          "认证翻译是译者或翻译社声明译文完整准确，没有第三方核实译者身份；公证翻译在此之上再加一层：由公证律师见证并认证该声明上的签名。公证从不核实译文内容是否准确，只核实是谁签的名。大学与多数私人机构接受单纯的认证翻译；使馆、外国法院与登记机关通常要求加上公证层，部分还要再加外交部认证。",
        rows: [
          { aspect: "证明的内容", a: "译者声明译文完整、准确", b: "签署认证声明者的身份与签名" },
          { aspect: "签署人", a: "译者或翻译公司代表", b: "泰国律师公会核准的公证律师" },
          { aspect: "一般时效", a: "当天至 2 个工作日", b: "另加约 1 个工作日" },
          { aspect: "一般费用", a: "仅按页计的翻译费", b: "翻译费加按签名计的认证费" },
          { aspect: "通常足够用于", a: "大学、雇主、银行、公司内部存档", b: "使馆、外国法院、公司登记机关、移民机关" },
          { aspect: "后续步骤", a: "常需再补公证", b: "可能仍需外交部与目的地使馆认证" },
        ],
        chooseA: [
          "收件方书面要求只写「certified translation」",
          "文件留在泰国境内或交给私人机构",
          "需要最快最省的方案，日后被要求时再补认证",
        ],
        chooseB: [
          "对方要求 notary、公证或「宣誓」签署",
          "文件将提交外国法院、登记机关或使馆",
          "下一步是外交部或使馆认证",
        ],
        faqs: [
          {
            q: "公证是否代表译文一定准确？",
            a: "不代表。公证认证的是谁签署了准确性声明，不是用词质量；准确性仍由签名的译者负责。这是我们最常需要向客户澄清的误解。",
          },
          {
            q: "同一个人可以既翻译又公证吗？",
            a: "实务上是两个角色：译者签署准确性声明，公证律师认证该签名。我们会同时安排，确保文件上的印章体系一致。",
          },
          {
            q: "使馆会接受没有公证的认证翻译吗？",
            a: "有些会、有些不会，完全取决于该使馆与文件类型。请务必取得书面要求，因为使馆格式常在未预告的情况下调整。",
          },
          {
            q: "加上公证要多少钱？",
            a: "按签名计收律师认证费，通常再加约一个工作日。金额取决于签名与页数，看到文件后才能准确报价。",
          },
          {
            q: "两种做法都需要外交部认证吗？",
            a: "文件要送往国外时通常需要。领事司按印章收费：普通件每枚 200 泰铢，加急每枚 400 泰铢。",
          },
          {
            q: "可以改用海牙认证（Apostille）吗？",
            a: "目前还不行。泰国已于 2026 年 6 月 30 日加入海牙认证公约，对泰国生效日为 2027 年 2 月 28 日；在此之前仍走外交部加使馆的链条。",
          },
        ],
      },
      {
        slug: "mfa-legalisation-vs-lawyer-certification",
        thSlug: "รับรองคำแปล-กงสุล-vs-ทนาย",
        title: "外交部领事认证 vs 律师认证译文",
        description:
          "对比泰国外交部领事司对译文的认证与公证律师认证：费用、时效、语种限制，以及使馆通常要求哪一种。",
        h1: "外交部认证 vs 律师认证：译文该走哪一层？",
        aLabel: "外交部领事认证",
        bLabel: "律师认证",
        answer:
          "外交部领事司对泰英之间的译文（及泰文正本）作出政府层级的认证，普通件每枚印章 200 泰铢、加急每枚 400 泰铢；公证律师则以私人专业身份认证签名与副本，可覆盖任何语种且速度快得多。外国政府机关与目的地使馆通常要求外交部印章；私人收件方与后续公证链条往往只需律师这一层。",
        rows: [
          { aspect: "行为性质", a: "外交部的政府认证", b: "持照律师的专业认证" },
          { aspect: "官方费用", a: "普通每枚 200 泰铢，加急每枚 400 泰铢", b: "按签名计的专业服务费，逐件报价" },
          { aspect: "一般时效", a: "普通约 2–3 个工作日，加急当天或次日", b: "预约后多为当天或次日" },
          { aspect: "语种覆盖", a: "以泰文与英文为主", b: "可安排合格译者的任何语种" },
          { aspect: "接受方", a: "目的地使馆与外国政府机关", b: "私人收件方，以及作为外交部前置步骤" },
          { aspect: "链条位置", a: "通常在翻译与律师认证之后", b: "通常是第一层认证" },
        ],
        chooseA: [
          "外国政府机关或使馆要求外交部（领事）认证",
          "文件为泰英互译且将在境外提交",
          "下一步是在曼谷的目的地使馆做认证",
        ],
        chooseB: [
          "所需语种外交部不常规受理",
          "收件方为私人机构或海外律师",
          "需要在外交部预约前先取得快速的第一层认证",
        ],
        faqs: [
          {
            q: "可以跳过律师认证直接去外交部吗？",
            a: "视文件而定。许多文件需先附上符合外交部格式的认证译文，因此我们会在预约前先检查文件。",
          },
          {
            q: "加急服务值得吗？",
            a: "加急每枚印章 400 泰铢（普通 200 泰铢），可明显缩短等待。若递交期限紧张，通常整体反而更省。",
          },
          {
            q: "外交部会认证中文或日文译文吗？",
            a: "领事认证以泰文与英文为主。其他目标语言的实务做法是出具认证译文并由律师认证，再对泰文或英文版本做领事认证。",
          },
          {
            q: "两层都需要吗？",
            a: "多数送往国外的文件是的：先律师认证、再外交部、最后目的地使馆。我们会依收件机构的书面要求确认顺序后再开始。",
          },
          {
            q: "海牙认证会取代外交部印章吗？",
            a: "自 2027 年 2 月 28 日公约对泰国生效后，对成员国而言 Apostille 将取代使馆那一步；在此之前完整链条仍然适用。",
          },
          {
            q: "会被收几枚印章的费用？",
            a: "外交部按印章计费，多份文件会成倍增加。把清单发给我们，可先估算印章数量。",
          },
        ],
      },
      {
        slug: "naati-vs-thai-certified-translation",
        thSlug: "naati-vs-คำแปลรับรองไทย",
        title: "NAATI 翻译 vs 泰国认证翻译（送澳洲用）",
        description:
          "NAATI 是澳洲的翻译资格认证机构。对比 NAATI 认证翻译与泰国认证并经领事认证的译文，看清哪些机构接受哪一种。",
        h1: "NAATI 翻译 vs 泰国认证翻译",
        aLabel: "NAATI 认证翻译",
        bLabel: "泰国认证翻译",
        answer:
          "NAATI 是澳洲全国性的翻译资格认证机构，NAATI 译文带有译员印章与编号，是澳洲内政部等政府机关的默认要求；泰国认证翻译（即使加上律师认证与外交部认证）走的是泰国链条，面向泰国机关与第三国收件方。两者没有绝对优劣，取决于最终读这份文件的机构。",
        rows: [
          { aspect: "资格机构", a: "澳洲 NAATI（naati.com.au）", b: "泰国无登记制度，由译者或翻译社自行认证" },
          { aspect: "可见标记", a: "带编号与有效期的译员印章", b: "认证声明、译者签名、翻译社印章" },
          { aspect: "主要受众", a: "澳洲政府、大学、法院", b: "泰国机关、驻曼谷使馆、第三国" },
          { aspect: "是否需再认证", a: "在澳洲境内使用通常不需要", b: "常需再经律师认证与外交部认证" },
          { aspect: "出具地点", a: "由持证译员出具，不限所在地", b: "在泰国出具，与泰国印章链条衔接" },
          { aspect: "一般时效", a: "取决于该语种译员的档期", b: "当天至数个工作日，另加认证时间" },
        ],
        chooseA: [
          "向澳洲政府部门或大学递交",
          "书面要求明确写明 NAATI",
          "文件在澳洲境内使用，而非在泰国出示",
        ],
        chooseB: [
          "收件方是泰国机关、驻曼谷使馆或非澳洲国家",
          "后续还要经外交部与目的地使馆",
          "所需语种没有可用的 NAATI 译员",
        ],
        faqs: [
          {
            q: "泰国政府机关认可 NAATI 吗？",
            a: "NAATI 是澳洲资格。泰国机关看的是符合本地格式的认证译文，因此 NAATI 译文若要在泰国使用，可能需要在本地重新认证。",
          },
          {
            q: "同一份文件可以两种认证都做吗？",
            a: "可以。有客户需要一份 NAATI 版本送澳洲、另一份泰国认证版本在本地使用，两者由同一原件分别制作。",
          },
          {
            q: "澳洲内政部一定要 NAATI 吗？",
            a: "其公开指引要求由具备适当资格的译者翻译，而 NAATI 是澳洲的标准资格。请以您所申请项目的具体指引文字为准。",
          },
          {
            q: "NAATI 译文需要外交部认证吗？",
            a: "在澳洲境内使用一般不需要。若同一份文件之后要交给泰国机关或第三国使馆，可能会被要求走泰国链条。",
          },
          {
            q: "哪一种比较快？",
            a: "两者本身没有快慢之分，瓶颈通常是该语种译员的档期与外交部排队情况。确认语种与目的地后我们会给出实际区间。",
          },
          {
            q: "不确定对方要哪一种怎么办？",
            a: "请对方以书面回复，再把回复发给我们。凭猜测选择是文件被重译最常见的原因。",
          },
        ],
      },
      {
        slug: "sworn-translator-vs-thailand",
        thSlug: "sworn-translator-vs-ไทย",
        title: "欧洲宣誓翻译 vs 泰国认证制度",
        description:
          "德国、法国、西班牙、意大利设有法院宣誓翻译制度，泰国没有。对比两套制度，并说明如何从曼谷满足欧洲机构的要求。",
        h1: "欧洲宣誓翻译 vs 泰国认证制度",
        aLabel: "欧洲宣誓翻译",
        bLabel: "泰国认证链条",
        answer:
          "若干欧洲国家由法院或部会指定宣誓翻译，其译文可直接被本国机关接受；泰国没有对应的登记制度。因此送往这些国家的泰国文件，通常做法是出具认证译文、由公证律师认证、再经外交部与目的地使馆认证；或先把泰文正本做完认证，再到目的地由当地宣誓翻译重新翻译。",
        rows: [
          { aspect: "法律依据", a: "依各国法律由法院或部会指定", b: "无宣誓翻译登记，依合约与专业规范认证" },
          { aspect: "签署人", a: "宣誓翻译本人（宣誓下签署）", b: "译者加公证律师" },
          { aspect: "可直接被接受", a: "指定国境内的机关", b: "泰国机关；经认证后可供境外使用" },
          { aspect: "作业地点", a: "多在目的地国家", b: "在泰国、文件出境前完成" },
          { aspect: "正本认证", a: "泰文正本通常仍须认证", b: "外交部认证每枚 200 泰铢、加急 400 泰铢" },
          { aspect: "选错的风险", a: "非登记译者所译可能被拒", b: "抵达后可能仍须由当地宣誓翻译重译" },
        ],
        chooseA: [
          "目的地机关明确要求法院或部会指定的宣誓翻译",
          "在目的地有可代为安排的联系人或代理",
          "文件篇幅短，抵达后重译成本很低",
        ],
        chooseB: [
          "泰文正本无论如何都要在出境前完成认证",
          "目的地接受经认证的泰国认证译文",
          "希望出发前把整套文件准备齐全",
        ],
        faqs: [
          {
            q: "泰国译者可以成为宣誓翻译吗？",
            a: "依泰国法律不行，泰国没有这项登记制度。部分泰籍人士在其他国家依当地规则取得指定，那属于该国制度。",
          },
          {
            q: "德国会接受泰国认证译文吗？",
            a: "德国机关通常要求由在德国宣誓的译者翻译，各邦与各机关做法不一。付款前请先书面询问该具体机关。",
          },
          {
            q: "在国外翻译，泰文正本还要做认证吗？",
            a: "通常仍要。目的地需要证明泰文文件本身为真，而这正是外交部与使馆认证的作用。",
          },
          {
            q: "2027 年的海牙认证会改变这点吗？",
            a: "自 2027 年 2 月 28 日起，对公约成员国而言 Apostille 将取代使馆认证，证明正本更简便；宣誓翻译的要求属另一回事，不受影响。",
          },
          {
            q: "实际费用大约多少？",
            a: "费用分两侧：泰国这侧的翻译费、律师认证费与每枚 200 或 400 泰铢的外交部印章费；另一侧是目的地的翻译费用。我们只报泰国这侧。",
          },
          {
            q: "你们能同时协调两侧吗？",
            a: "我们负责把泰国这侧做完并交付一整套文件，让目的地的宣誓翻译可直接据以作业，避免重复劳动。",
          },
        ],
      },
      {
        slug: "translate-into-english-vs-target-language",
        thSlug: "แปลเป็นอังกฤษ-vs-แปลเป็นภาษาปลายทาง",
        title: "该翻成英文还是翻成目的地语言？",
        description:
          "英文是泰国认证链条的枢纽语言，但许多机关只读本国语言。从费用、链条长度与被退件风险三方面对比。",
        h1: "翻成英文 vs 翻成目的地语言",
        aLabel: "翻成英文",
        bLabel: "翻成目的地语言",
        answer:
          "英文是泰国文件作业的枢纽语言：外交部领事司常规受理泰英互译，因此英文译本在链条中阻力最小。但只读本国语言的目的地机关可能拒收英文版，或要求抵达后再译一次。最省钱的原则是严格照书面要求执行：要求写明语言就翻成该语言，英文只作为取得认证印章的枢纽。",
        rows: [
          { aspect: "外交部认证", a: "泰英互译为常规受理", b: "多半无法受理，改认证泰文或英文版本" },
          { aspect: "链条长度", a: "最短：一次翻译、一条链条", b: "较长，可能还要在目的地再译一次" },
          { aspect: "费用", a: "多数文件每页较低", b: "较高，冷门语种尤其明显" },
          { aspect: "被退件风险", a: "对方只读本国语言时较高", b: "要求写明该语言时较低" },
          { aspect: "时效", a: "最快，译者供给充足", b: "取决于该语种档期" },
          { aspect: "适用对象", a: "驻曼谷使馆、国际学校、银行", b: "国外登记机关、法院与市政机关" },
        ],
        chooseA: [
          "要求写「English translation」或未指定语言",
          "下一步是外交部与驻曼谷使馆认证",
          "收件方是以英文作业的国际机构",
        ],
        chooseB: [
          "书面要求指明目的地语言",
          "文件将提交国外登记机关、法院或市政机关",
          "先前提交的英文版本已被退回",
        ],
        faqs: [
          {
            q: "可以两种语言都提交吗？",
            a: "通常可以，重要案件这样做也较保险。但请先确认对方接受双语文件包，而非只收单一语言。",
          },
          {
            q: "外交部会认证泰译日的译文吗？",
            a: "领事认证以泰文与英文为主。日文的常见做法是对泰文或英文版本做认证，并附上带认证声明的日文译文。",
          },
          {
            q: "经英文中转再翻译可以接受吗？",
            a: "常见且多半被接受，但每多一次中转就多一分术语偏移的风险。法律文书我们建议直接从泰文原文翻译。",
          },
          {
            q: "整体来说哪种更省？",
            a: "英文前期更省；一旦被目的地退件、必须在国外重译，就不再省了。",
          },
          {
            q: "怎么取得书面要求？",
            a: "向收件机关索取文件清单（邮件即可）或从其官方网站下载，发给我们后我们会完全比照格式制作。",
          },
          {
            q: "姓名拼写要与护照一致吗？",
            a: "必须一致。护照与译文姓名不符，是文件被退回最常见的原因之一。",
          },
        ],
      },
      {
        slug: "diy-translation-vs-professional-team",
        thSlug: "แปลเอง-vs-ใช้ทีมแปลรับรอง",
        title: "自己翻译 vs 交给认证翻译团队",
        description:
          "自行翻译在被退件之前都是免费的。从费用、可否认证、格式要求与返工风险四方面对比。",
        h1: "自己翻译 vs 使用认证翻译团队",
        aLabel: "自己翻译",
        bLabel: "认证翻译团队",
        answer:
          "您当然可以自己翻译，但几乎没有机关会接受由受益人自己认证的译文；而且泰国官方文件有固定版式——印章、登记编号、签发机关——都必须在译文中如实呈现。认证团队会签署准确性声明、按外交部与使馆期待的格式制作，并可衔接律师认证与领事认证。真正要比的不是「收费 vs 免费」，而是收费与一次退件成本之间的差额。",
        rows: [
          { aspect: "前期费用", a: "无", b: "按页报价，开工前确认" },
          { aspect: "谁能认证", a: "申请人本人几乎都不被接受", b: "译者或翻译社，再由公证律师认证" },
          { aspect: "格式合规", a: "常漏印章、登记编号与版式", b: "依外交部与使馆期待的版式制作" },
          { aspect: "能否送认证", a: "多在柜台即被退回", b: "按可通过外交部查核的标准制作" },
          { aspect: "返工风险", a: "高，一次退件可能耽误数周", b: "低，问题在递交前处理" },
          { aspect: "适用情形", a: "个人理解内容、内部草稿", b: "任何要正式递交的文件" },
        ],
        chooseA: [
          "只需要看懂文件内容",
          "收件方明确允许未经认证的译文",
          "属于内部草稿且无递交期限",
        ],
        chooseB: [
          "文件要递交任何政府机关或使馆",
          "后续还要做律师认证或外交部认证",
          "有期限压力，退件代价很高",
        ],
        faqs: [
          {
            q: "我自己翻译再请律师认证可以吗？",
            a: "律师认证的是准确性声明上的签名；而申请人为自己的佐证文件作认证，通常会被收件方拒绝。由独立第三方认证可避免此争议。",
          },
          {
            q: "机器翻译可以吗？",
            a: "正式递交不行。除准确性外，泰国官方文件的印章、登记编号与版式，机器输出无法正确呈现。",
          },
          {
            q: "文件被退回最常见的原因是什么？",
            a: "姓名拼写与护照不符、未呈现印章与钢印、以及采用了对方不接受的认证格式。",
          },
          {
            q: "专业翻译要多少钱？",
            a: "按页计价，视语种与文件复杂度而定。发张照片过来即可先取得报价，再决定是否开工。",
          },
          {
            q: "已有的译文能帮忙修改吗？",
            a: "可以。内容尚可者我们审校后重新认证；若品质不足，重译通常比逐句修改更快。",
          },
          {
            q: "整条链条要多久？",
            a: "概略而言：翻译 1–2 个工作日、律师认证约 1 天、外交部普通件 2–3 个工作日（加急更快），之后再看目的地使馆的规定。",
          },
        ],
      },
    ],
  },
};

/** Thai slug → ASCII locale slug, used to build reciprocal hreflang. */
export const MLT_COMPARE_SLUG_MAP: Record<string, string> = Object.fromEntries(
  MLT_COMPARE_LOCALE.en.items.map((i) => [i.thSlug, i.slug]),
);
